/**
 * Proposals Inbox Page (Phase 23.2.1 — Living SOPs)
 *
 * The trainer review queue for AI-authored + trainer-authored SOP draft
 * proposals. Grid of cards with filter/sort/pagination, matching the
 * PendingReviewPage layout (see apps/web/src/pages/assessments/PendingReviewPage.tsx).
 *
 * Navigation:
 *  - Card click → /sops/proposals/:id (lands in Chunk 23.2.2)
 *  - Deep-link from SOP Health pill → /sops/proposals?sopId=<uuid>
 *
 * Role gate: manager (3) or higher. Full Approve actions land in Chunk
 * 23.2.3 with per-role server-side checks.
 */

import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Inbox,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  ShieldAlert,
  GitCompare,
  Activity,
  MessageSquare,
  UserPen,
  ChevronRight as ChevronRightIcon,
  Eye,
} from 'lucide-react';
import {
  useSopProposals,
  type SopProposalListItem,
  type SopProposalsQueryParams,
} from '../../hooks/useSopProposals';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { useRoleLevel } from '../../lib/hooks/useRoleLevel';
import { getScoreColor } from '../../lib/score-colors';
import type {
  SopDraftProposalState,
  SopDraftProposalSource,
  ProposalImpactBucket,
  ProposalAgeBucket,
  ProposalInboxSort,
} from '@guild/shared';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Source badge colors (blueprint §Chunk 23.2.1: struggle_signal purple,
// performance_delta amber, coach_mining blue, manual gray).
const SOURCE_BADGE_STYLES: Record<
  SopDraftProposalSource,
  { bg: string; text: string; icon: typeof Sparkles; label: string }
> = {
  struggle_signal: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    icon: AlertCircle,
    label: 'Struggle Signal',
  },
  performance_delta: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: Activity,
    label: 'Performance Delta',
  },
  coach_mining: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    icon: MessageSquare,
    label: 'Coach Mining',
  },
  manual: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    icon: UserPen,
    label: 'Manual',
  },
};

function SourceBadge({ source }: { source: SopDraftProposalSource }) {
  const style = SOURCE_BADGE_STYLES[source];
  const Icon = style.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${style.bg} ${style.text}`}
    >
      <Icon size={12} strokeWidth={2.5} />
      {style.label}
    </span>
  );
}

// Impact ring: reuses score-colors.ts emerald/amber/red thresholds.
function ImpactRing({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const ringClass = getScoreColor(clamped, 'text');
  const bgClass = getScoreColor(clamped, 'bg');
  return (
    <div
      className={`flex h-12 w-12 flex-col items-center justify-center rounded-xl ${bgClass}`}
      title={`Impact score: ${clamped}`}
    >
      <span className={`text-sm font-bold tabular-nums ${ringClass}`}>
        {clamped}
      </span>
      <span className="text-[9px] uppercase tracking-wide text-gray-500">
        Impact
      </span>
    </div>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────

function ProposalCard({
  proposal,
  onClick,
}: {
  proposal: SopProposalListItem;
  onClick: () => void;
}) {
  const evidenceCount = Array.isArray(proposal.evidence)
    ? proposal.evidence.length
    : 0;
  const confidencePct = Math.round((proposal.confidence ?? 0) * 100);
  const sopUnavailable = !proposal.sopTitle;

  return (
    <button
      onClick={onClick}
      className="group relative flex w-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-300 hover:border-guild-green/20 hover:shadow-lg hover:shadow-guild-green/5"
    >
      {/* Top row: source badge + impact ring */}
      <div className="flex items-start justify-between gap-3">
        <SourceBadge source={proposal.source} />
        <ImpactRing score={proposal.impactScore} />
      </div>

      {/* Breadcrumb */}
      <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
        <span className="truncate font-medium text-gray-900">
          {proposal.sopTitle ?? 'SOP unavailable'}
        </span>
        {proposal.breadcrumb.sectionTitle && (
          <>
            <ChevronRightIcon size={12} className="flex-shrink-0 text-gray-400" />
            <span className="truncate">
              {proposal.breadcrumb.sectionTitle}
            </span>
          </>
        )}
        {proposal.breadcrumb.taskTitle && (
          <>
            <ChevronRightIcon size={12} className="flex-shrink-0 text-gray-400" />
            <span className="truncate">{proposal.breadcrumb.taskTitle}</span>
          </>
        )}
      </div>

      {/* Rationale preview (2-line clamp) */}
      <p className="mt-3 line-clamp-2 text-sm text-gray-600">
        {proposal.rationale || 'No rationale provided.'}
      </p>

      {/* Warnings row */}
      {(proposal.safetyCheckPassed === false || proposal.conflictDetected) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {proposal.safetyCheckPassed === false && (
            <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
              <ShieldAlert size={11} strokeWidth={2.5} />
              Safety blocked
            </span>
          )}
          {proposal.conflictDetected && (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              <GitCompare size={11} strokeWidth={2.5} />
              Conflict detected
            </span>
          )}
        </div>
      )}

      {/* Evidence row (placeholder — 23.4.2 signs real thumbnails) */}
      {evidenceCount > 0 ? (
        <div className="mt-3 flex items-center gap-2">
          {Array.from({ length: Math.min(evidenceCount, 3) }).map((_, i) => (
            <div
              key={i}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100"
              aria-hidden="true"
            >
              <ImageIcon size={14} className="text-gray-400" />
            </div>
          ))}
          {evidenceCount > 3 && (
            <span className="text-xs text-gray-500">+{evidenceCount - 3}</span>
          )}
        </div>
      ) : null}

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
        <span className="text-xs text-gray-500">
          {formatRelativeTime(proposal.createdAt)}
        </span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
          {confidencePct}% confidence
        </span>
      </div>

      {sopUnavailable && (
        <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
          This SOP was removed. Approve blocked.
        </div>
      )}
    </button>
  );
}

// ── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({
  filters,
  onChange,
}: {
  filters: SopProposalsQueryParams;
  onChange: (next: SopProposalsQueryParams) => void;
}) {
  const update = (patch: Partial<SopProposalsQueryParams>) => {
    onChange({ ...filters, ...patch, page: 1 });
  };

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      {/* Status is now owned by the tab bar above — tabs are canonical for
          filters.state. Removed the dropdown to eliminate the dual-source
          conflict flagged during 23.2.7 review (tab says pending_review,
          dropdown could override to rejected). */}

      {/* Source */}
      <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Source
        <select
          value={filters.source ?? ''}
          onChange={(e) =>
            update({
              source: (e.target.value || undefined) as
                | SopDraftProposalSource
                | undefined,
            })
          }
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
        >
          <option value="">All sources</option>
          <option value="struggle_signal">Struggle Signal</option>
          <option value="performance_delta">Performance Delta</option>
          <option value="coach_mining">Coach Mining</option>
          <option value="manual">Manual</option>
        </select>
      </label>

      {/* Impact */}
      <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Impact
        <select
          value={filters.impactBucket ?? ''}
          onChange={(e) =>
            update({
              impactBucket: (e.target.value || undefined) as
                | ProposalImpactBucket
                | undefined,
            })
          }
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
        >
          <option value="">Any</option>
          <option value="high">High (70+)</option>
          <option value="medium">Medium (40-69)</option>
          <option value="low">Low (&lt;40)</option>
        </select>
      </label>

      {/* Age */}
      <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Age
        <select
          value={filters.ageBucket ?? ''}
          onChange={(e) =>
            update({
              ageBucket: (e.target.value || undefined) as
                | ProposalAgeBucket
                | undefined,
            })
          }
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
        >
          <option value="">Any age</option>
          <option value="<24h">Less than 24h</option>
          <option value="24-72h">24-72h</option>
          <option value="72h+">72h+</option>
        </select>
      </label>

      {/* Sort */}
      <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Sort
        <select
          value={filters.sort ?? 'impactScore'}
          onChange={(e) =>
            update({ sort: e.target.value as ProposalInboxSort })
          }
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
        >
          <option value="impactScore">Impact (highest first)</option>
          <option value="createdAt">Newest first</option>
          <option value="confidence">Confidence (highest first)</option>
        </select>
      </label>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

// ── Tabs ────────────────────────────────────────────────────────────────────
//
// Phase 23.2.7: two tabs — "Pending review" (default) and "Recently approved"
// (filters state=approved + client-side approvedAt >= 7d ago via sort order).
// Available to both trainers (action surface) and managers (read-only).

type InboxTab = 'pending_review' | 'recently_approved';

const TAB_DEFS: Array<{
  id: InboxTab;
  label: string;
  state: SopDraftProposalState;
}> = [
  { id: 'pending_review', label: 'Pending review', state: 'pending_review' },
  { id: 'recently_approved', label: 'Recently approved (7d)', state: 'approved' },
];

/** Seven-day cutoff for the "Recently approved" tab client-side filter. */
const RECENTLY_APPROVED_CUTOFF_MS = 7 * 24 * 60 * 60 * 1000;

export default function ProposalsInboxPage() {
  useDocumentTitle('SOP Proposals');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isManager, isTrainerOrHigher } = useRoleLevel();

  // Seed filters from URL (supports the SOP Health pill deep-link pattern
  // from §3.5 of the blueprint: /sops/proposals?sopId=X).
  const initialSopId = searchParams.get('sopId') ?? undefined;
  // Seed tab from URL so browser back/forward works.
  const initialTab: InboxTab =
    (searchParams.get('tab') as InboxTab | null) === 'recently_approved'
      ? 'recently_approved'
      : 'pending_review';

  const [activeTab, setActiveTab] = useState<InboxTab>(initialTab);

  const activeTabDef = useMemo(
    () => TAB_DEFS.find((t) => t.id === activeTab) ?? TAB_DEFS[0],
    [activeTab]
  );

  const [filters, setFilters] = useState<SopProposalsQueryParams>({
    state: activeTabDef.state,
    sort: activeTab === 'recently_approved' ? 'createdAt' : 'impactScore',
    sopId: initialSopId,
    page: 1,
    limit: 12,
  });

  // Keep sopId + tab in URL so deep-links round-trip.
  const setFiltersAndUrl = (next: SopProposalsQueryParams) => {
    setFilters(next);
    const params = new URLSearchParams(searchParams);
    if (next.sopId) {
      params.set('sopId', next.sopId);
    } else {
      params.delete('sopId');
    }
    setSearchParams(params, { replace: true });
  };

  const handleTabChange = (tab: InboxTab) => {
    const def = TAB_DEFS.find((t) => t.id === tab) ?? TAB_DEFS[0];
    setActiveTab(tab);
    // Reset filters when switching tabs: new state, page 1. Sort defaults:
    // - Pending: impactScore (highest-impact first)
    // - Recently approved: createdAt (newest first — approvedAt isn't a
    //   sort option server-side, but recent-approvals also tend to be
    //   recent-created; the 7d client-side filter keeps the list scoped).
    const nextFilters: SopProposalsQueryParams = {
      ...filters,
      state: def.state,
      sort: tab === 'recently_approved' ? 'createdAt' : 'impactScore',
      page: 1,
    };
    setFilters(nextFilters);
    const params = new URLSearchParams(searchParams);
    if (tab === 'pending_review') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params, { replace: true });
  };

  const { data, isLoading, error } = useSopProposals(filters);

  // Client-side 7-day filter for the "Recently approved" tab. The server
  // returns everything in state=approved; we trim to the 7-day window here
  // so we don't need a new query param.
  //
  // TODO(23.2.7-followup): if trainers report missing recent approvals due
  // to pagination boundaries (e.g., approvals from day 8 pushing day 6
  // approvals to page 2 where the 7-day filter runs first), promote the
  // 7-day filter to the server with a `approvedWithinDays` query param.
  // Confidence: medium — this depends on approval cadence per org.
  const filteredProposals = useMemo(() => {
    const list = data?.data ?? [];
    if (activeTab !== 'recently_approved') return list;
    const cutoff = Date.now() - RECENTLY_APPROVED_CUTOFF_MS;
    return list.filter((p) => {
      // We don't have approvedAt on the list shape — fall back to createdAt.
      // approvedAt is stamped on the row but not selected in the list query;
      // for 23.2.7 the createdAt proxy is acceptable because an approved
      // proposal's approvedAt is always >= createdAt, so filtering by
      // createdAt >= cutoff is a STRICTER (never false-positive) filter.
      const createdMs = new Date(p.createdAt).getTime();
      return Number.isFinite(createdMs) && createdMs >= cutoff;
    });
  }, [data, activeTab]);

  const proposals = filteredProposals;
  const pagination = data?.pagination;
  const meta = data?.meta;
  const totalPages = pagination?.totalPages ?? 1;
  const page = filters.page ?? 1;

  const handleCardClick = (p: SopProposalListItem) => {
    navigate(`/sops/proposals/${p.id}`);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
            <Inbox size={24} className="text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              SOP Proposals
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {isTrainerOrHigher
                ? 'AI-authored and trainer-authored edits awaiting review'
                : 'AI-authored and trainer-authored edits — read-only view'}
            </p>
          </div>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="flex items-center gap-2 rounded-xl bg-guild-mint/40 px-4 py-2">
            <Sparkles size={16} className="text-guild-green" />
            <span className="text-sm font-medium text-guild-green">
              {pagination.total}{' '}
              {pagination.total === 1 ? 'proposal' : 'proposals'}
            </span>
          </div>
        )}
      </div>

      {/* Phase 23.2.7: Manager read-only ribbon. Trainers (4+) and admins see
          the full action surface on the detail page; managers (3) browse the
          queue for awareness but can't approve/reject/revise. */}
      {isManager && (
        <div
          role="status"
          className="mb-4 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3"
        >
          <Eye size={18} className="mt-0.5 flex-shrink-0 text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-blue-900">
              Read-only — viewing as manager
            </p>
            <p className="mt-0.5 text-xs text-blue-800">
              Approve, Reject, and Revise actions are restricted to trainers
              and admins. You can review proposals for awareness.
            </p>
          </div>
        </div>
      )}

      {/* Phase 23.2.7: Tab bar — Pending review / Recently approved (7d).
          Available to all roles; the ribbon above clarifies manager-only
          limitations on the review surface. */}
      <div
        className="mb-4 flex items-center gap-1 border-b border-gray-200"
        role="tablist"
        aria-label="Proposal status tabs"
      >
        {TAB_DEFS.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => handleTabChange(tab.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                selected
                  ? 'border-guild-green text-guild-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* At-capacity banner */}
      {meta?.atCapacity && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle
              size={20}
              className="mt-0.5 flex-shrink-0 text-amber-600"
            />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Queue at capacity
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                {meta.pendingCount} pending proposals (max {meta.maxPending}).
                New proposals are pausing at ingestion until the queue clears.
                Review or reject a few to resume.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-6">
        <FilterBar filters={filters} onChange={setFiltersAndUrl} />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-52 animate-pulse rounded-2xl border border-gray-100 bg-gray-50"
            />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50">
            <AlertCircle size={32} className="text-rose-500" />
          </div>
          <p className="mt-4 text-sm font-medium text-gray-900">
            Failed to load proposals
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Please try refreshing the page
          </p>
        </div>
      ) : proposals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CheckCircle2 size={40} className="text-emerald-500" />
            </div>
            <div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 shadow-lg">
              <Sparkles size={16} className="text-white" />
            </div>
          </div>
          <h3 className="mt-6 text-lg font-semibold text-gray-900">
            {activeTab === 'recently_approved'
              ? 'No proposals approved in the last 7 days'
              : 'No proposals pending review'}
          </h3>
          <p className="mt-1 max-w-sm text-center text-sm text-gray-500">
            {activeTab === 'recently_approved'
              ? 'Approvals from the past week will show up here. Switch to Pending review to see what’s waiting.'
              : 'New proposals will appear here automatically. Adjust the filters above to view approved, rejected, or superseded proposals.'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {proposals.map((p) => (
              <ProposalCard
                key={p.id}
                proposal={p}
                onClick={() => handleCardClick(p)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-6 py-4 shadow-sm">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setFiltersAndUrl({
                      ...filters,
                      page: Math.max(1, page - 1),
                    })
                  }
                  disabled={page === 1}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button
                  onClick={() =>
                    setFiltersAndUrl({
                      ...filters,
                      page: Math.min(totalPages, page + 1),
                    })
                  }
                  disabled={page === totalPages}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
